import React from "react";
import { Alert, MenuItem, Select, ListItemText } from "@mui/material";
import { parseListConfig, serializeListConfig, decodeVarbinary, productMacros, urlLengthError, SAVE_FAILED_MESSAGE } from "../../lib/destinationUtils";

const methods = [
  { name: "POST", value: "POST" },
  { name: "GET", value: "GET" },
];

interface ProductEventsConfigProps {
  clientId: number;
  config: any;
  saveDestination: any;
  deleteDestination: any;
  history: any;
}

interface ProductEventsConfigState {
  editing: boolean;
  confirmRemove: boolean;
  klaviyoDirect: boolean;
  form: any;
  saving: boolean;
  saveError: string | null;
  saved: boolean;
}

const blankForm = (clientId) => ({
  clientId: clientId,
  name: 'Product events',
  type: 'product_view',
  method: 'POST',
  url: '',
  body: '{}',
  headers: '',
  apiKey: '',
  customProperties: '',
  paused: 0,
});

// Turns a stored destination row into form state. VARBINARY columns arrive as
// Buffer JSON, so they must be decoded before they touch an input.
const formFromConfig = (config, clientId) => {
  if (!config) return blankForm(clientId);
  return {
    id: config.id,
    clientId: config.clientId || clientId,
    name: config.name || 'Product events',
    type: 'product_view',
    method: config.method || 'POST',
    url: config.url || '',
    body: config.body || '{}',
    headers: config.headers || '',
    apiKey: decodeVarbinary(config.apiKey),
    customProperties: decodeVarbinary(config.customProperties),
    paused: Number(config.paused) || 0,
  };
};

class ProductEventsConfig extends React.Component<ProductEventsConfigProps, ProductEventsConfigState> {
  constructor(props) {
    super(props);
    const form = formFromConfig(props.config, props.clientId);
    this.state = {
      editing: false,
      confirmRemove: false,
      klaviyoDirect: !!String(form.apiKey || '').trim(),
      form: form,
      saving: false,
      saveError: null,
      saved: false,
    };
  }

  // The destinations list arrives after first paint (and changes when the
  // header client picker switches accounts) — resync unless mid-edit.
  public componentDidUpdate(prev: ProductEventsConfigProps) {
    const changed = prev.config !== this.props.config || prev.clientId !== this.props.clientId;
    if (prev.clientId !== this.props.clientId && (this.state.saved || this.state.saveError)) {
      this.setState({ saved: false, saveError: null });
    }
    if (changed && !this.state.editing) {
      const form = formFromConfig(this.props.config, this.props.clientId);
      this.setState({ form: form, klaviyoDirect: !!String(form.apiKey || '').trim() });
    }
  }

  private set = (name, value) => {
    this.setState({ form: Object.assign({}, this.state.form, { [name]: value }), saved: false });
  };

  private insertMacro = (macro) => {
    this.set('url', String(this.state.form.url || '') + '{{' + macro + '}}');
  };

  private save = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    let dest = Object.assign({}, this.state.form);
    // Unchecking Klaviyo direct must clear the stored key/lists: the poller
    // prefers api_key over url, so a leftover key would silently override the
    // webhook config being saved.
    if (!this.state.klaviyoDirect) {
      dest.apiKey = '';
      dest.customProperties = '';
    }
    if (this.state.saving) return;
    const tooLong = urlLengthError(dest.url);
    if (tooLong) {
      this.setState({ saveError: tooLong, saved: false });
      return;
    }
    // Leave edit mode only once the save is confirmed; on failure keep the
    // typed values and say so (a failed save used to look like a success).
    this.setState({ saving: true, saveError: null, saved: false });
    Promise.resolve(this.props.saveDestination(dest, this.props.history))
      .then(() => this.setState({ saving: false, editing: false, form: dest, saved: true }))
      .catch(() => this.setState({ saving: false, saveError: SAVE_FAILED_MESSAGE }));
  };

  private remove = () => {
    if (this.state.form.id) {
      this.props.deleteDestination(this.state.form.id);
    }
    this.setState({ confirmRemove: false, editing: false, form: blankForm(this.props.clientId), klaviyoDirect: false });
  };

  public render(): JSX.Element {
    const { form, klaviyoDirect, editing, confirmRemove } = this.state;
    const configured = !!form.id;
    const lists = parseListConfig(form.customProperties);
    const paused = Number(form.paused) === 1;

    if (!configured && !editing) {
      return (
        <div className="pe_card">
          <p className="pe_intro">
            Product events send this client's product views and add to cart activity
            to their marketing platform, for identified visitors only. They are not
            billed and do not use recognition credits.
          </p>
          <button className="button accent" onClick={() => this.setState({ editing: true })}>
            Set up product events
          </button>
        </div>
      );
    }

    return (
      <div className="pe_card">
        <div className="pe_status_row">
          <span className={paused ? 'pe_badge pe_badge--paused' : 'pe_badge pe_badge--active'}>
            {paused ? 'Paused' : 'Active'}
          </span>
          <span className="pe_status_text">
            {paused
              ? 'Nothing is being sent for this client. Product views during a pause are not stored. Add to cart activity from the last 24 hours is delivered when you make it active again.'
              : 'Product views and add to cart events are delivered for identified visitors while the account has lead credits.'}
          </span>
        </div>

        <form noValidate autoComplete="off" onSubmit={(event) => this.save(event)}>
          <div className="flexBox">
            <div id="w50" className="mb-4">
              <label className="field_label">Name</label>
              <input
                name="name"
                type="text"
                value={form.name || ''}
                onChange={(event) => this.set('name', event.target.value)}
              />
            </div>

            <div id="w50" className="mb-4">
              <label className="field_label">Status</label>
              <label className="pe_check">
                <input
                  name="active"
                  type="checkbox"
                  checked={!paused}
                  onChange={(event) => this.set('paused', event.target.checked ? 0 : 1)}
                />
                Active
              </label>
              <small>Uncheck to pause delivery. The setup below is kept, including the API key.</small>
            </div>

            <div id="w100" className="mb-4">
              <label className="pe_check">
                <input
                  name="klaviyoDirect"
                  type="checkbox"
                  checked={klaviyoDirect}
                  onChange={(event) => this.setState({ klaviyoDirect: event.target.checked })}
                />
                Use Klaviyo direct integration (no Zapier needed)
              </label>
            </div>

            {klaviyoDirect && (
              <div id="w100" className="mb-4">
                {/* Native Klaviyo delivery: with a key set the poller posts straight
                    to Klaviyo's Events API and subscribes to the lists below — the
                    webhook URL is not used in this mode. */}
                <label className="field_label">Klaviyo API Key</label>
                <input
                  name="apiKey"
                  type="text"
                  placeholder="pk_… private key (write scopes: Events, Profiles, Lists, Subscriptions)"
                  value={form.apiKey || ''}
                  onChange={(event) => this.set('apiKey', event.target.value)}
                />
                {!String(form.apiKey || '').trim() && (
                  <small>API key is required for direct delivery.</small>
                )}
                <label className="field_label">Klaviyo View List ID (optional)</label>
                <input
                  name="viewListId"
                  type="text"
                  placeholder="short code from the list URL, e.g. WPt6Td"
                  value={lists.viewList}
                  onChange={(event) => this.set('customProperties', serializeListConfig(event.target.value, lists.cartList))}
                />
                <label className="field_label">Klaviyo Cart List ID (optional)</label>
                <input
                  name="cartListId"
                  type="text"
                  placeholder="list for Added to Cart events"
                  value={lists.cartList}
                  onChange={(event) => this.set('customProperties', serializeListConfig(lists.viewList, event.target.value))}
                />
                <small>Klaviyo → Lists &amp; Segments → open the list → the short code in the URL. Use single opt-in lists: on a double opt-in list each visitor gets a Klaviyo confirmation email and joins only after clicking it. A blank list just skips list-adding for that event type — events still send. Anyone who unsubscribed is never re-added.</small>
              </div>
            )}

            {!klaviyoDirect && (<>
              <div id="w100" className="mb-4">
                <label className="field_label">URL</label>
                <input
                  name="url"
                  type="text"
                  value={form.url || ''}
                  onChange={(event) => this.set('url', event.target.value)}
                />
                <div className="options_buttons_area">
                  {/* type="button" is REQUIRED: these live inside a <form>, and an
                      HTML button defaults to type="submit". */}
                  {Object.entries(productMacros).map(([k, label]) => (
                    <button key={k} type="button" className="options_button" title={'{{' + k + '}}'}
                      onClick={() => this.insertMacro(k)}>
                      {label}
                    </button>
                  ))}
                </div>
                <small>Receives BOTH Viewed Product and Added to Cart. Split on the event_type field (or the {'{{eventType}}'} macro) inside the Zap/ESP.</small>
              </div>
              <div id="w50" className="mb-4">
                <label className="field_label">Headers</label>
                <input
                  name="headers"
                  type="text"
                  value={form.headers || ''}
                  onChange={(event) => this.set('headers', event.target.value)}
                />
              </div>
              <div id="w50" className="mb-4">
                <label className="field_label">Method</label>
                <div className="select_multi">
                  <Select
                    name="method"
                    value={form.method || 'POST'}
                    onChange={(event) => this.set('method', event.target.value)}
                    variant="outlined"
                  >
                    {methods.map((option) => (
                      <MenuItem key={option.name} value={option.value}>
                        <ListItemText primary={option.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
              <div id="w50" className="mb-4">
                <label className="field_label">Body</label>
                <input
                  name="body"
                  type="text"
                  value={form.body || ''}
                  onChange={(event) => this.set('body', event.target.value)}
                />
              </div>
            </>)}
          </div>
        </form>

        {this.state.saveError ? (
          <Alert severity="error" className="mb-4">{this.state.saveError}</Alert>
        ) : null}
        {this.state.saved ? (
          <Alert severity="success" className="mb-4">Saved.</Alert>
        ) : null}
        <div className="pe_actions">
          <button className="button accent" disabled={this.state.saving} onClick={(event) => this.save(event)}>
            {this.state.saving ? 'Saving...' : 'Save'}
          </button>
          {configured && !confirmRemove && (
            <button className="button secondary ml-3" onClick={() => this.setState({ confirmRemove: true })}>
              Remove setup
            </button>
          )}
          {confirmRemove && (
            <span className="pe_confirm">
              This deletes the setup and the stored API key. To stop delivery temporarily, uncheck Active instead.
              <button className="button accent ml-3" onClick={() => this.remove()}>Delete anyway</button>
              <button className="button secondary ml-3" onClick={() => this.setState({ confirmRemove: false })}>Cancel</button>
            </span>
          )}
        </div>
      </div>
    );
  }
}

export default ProductEventsConfig;
